import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { apiClient } from 'src/api/apiClient';
import {
  SESSION_STORAGE_ACCOUNT_KEY,
  setSessionStorageItemWithExpiration,
} from 'src/utility/sessionStorage';

export interface AccountSettings {
  enableNeedsAnalysis: boolean;
  enablePolicyReview: boolean;
  enableResourceCenter: boolean;
  enableRequestIllustration: boolean;
  enableSubmitCase: boolean;
  enableCaseStatusView: boolean;
  enableTermQuoterQuickLink: boolean;
  enableGULQuoterQuickLink: boolean;
  enableSPWLQuoterQuickLink: boolean;
  enableIULEstimatorQuickLink: boolean;
  enableULEstimatorQuickLink: boolean;
  enableLTCEstimatorQuickLink: boolean;
  enableTermCard: boolean;
  enableSPLCard: boolean;
  enableAssetBasedLTCCard: boolean;
  enableTraditionalLTCCard: boolean;
  enableWholeLifeCard: boolean;
  enableDisabilityCard: boolean;
  enableIUL: boolean;
  enableGUL: boolean;
  enableUL: boolean;
  enableVUL: boolean;
  enableWLSimplifiedIssue: boolean;
  enableWLTraditional: boolean;
  enableTermConversion: boolean;
  enableSaveMyQuote: boolean;
  enableSaveMyCase: boolean;
  enablePermPage: boolean;
  enablePermProductSelector: boolean;
  enablePermInsPlanningConcepts: boolean;
  enablePermPurchasingStatistics: boolean;
  enableCopyCase: boolean;
}

export interface AccountData {
  account: {
    accountId: number;
    accountName: string;
    domain: string;
    logoUrl: string;
    primaryColor: string;
    secondaryColor: string | null;
    oneClickAccountId: number;
    carrierCompensationStructure: string;
    authenticationRequired: boolean;
    stEmail: string;
    stPhone: string;
    atEmail: string;
    atPhone: string;
    cmtEmail: string;
    cmtPhone: string;
    platformSupportEmail: string;
    settings: AccountSettings;
  };
  authenticationRequiredByPass: boolean;
  isCovrproAccount: boolean;
  enableSearch: boolean;
  enableDownloadCSV: boolean;
  enableDownloadExcel: boolean;
  saveMyQuoteExpiryDays: string;
  saveMyCaseExpiryDays: string;
}

function resolveAccountDomain(): string {
  const fromQuery = new URLSearchParams(window.location.search).get('domain');
  const fromEnv = import.meta.env.VITE_ACCOUNT_DOMAIN as string | undefined;
  return fromQuery || fromEnv || window.location.hostname;
}

async function fetchAccount(): Promise<AccountData> {
  const response = await apiClient.get<AccountData>('/Account/GetAccount', {
    params: { Domain: resolveAccountDomain() },
  });
  setSessionStorageItemWithExpiration(SESSION_STORAGE_ACCOUNT_KEY, response.data, 60);
  return response.data;
}

export function useAccount() {
  return useQuery({
    queryKey: ['covr', 'account'],
    queryFn: fetchAccount,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useAccountSettings() {
  return useQuery({
    queryKey: ['covr', 'account'],
    queryFn: fetchAccount,
    staleTime: 30 * 60 * 1000,
    select: (data) => data.account.settings,
  });
}

// Mirrors advisor-3.0/src/components/Helpers/CheckAccess.js PermissionData.
// Used as fallback when a flag is absent from the account's settings payload.
const PermissionDefaults: Partial<Record<keyof AccountSettings, boolean>> = {
  enableNeedsAnalysis: false,
  enablePolicyReview: false,
  enableTermConversion: false,
  enableResourceCenter: true,
  enableRequestIllustration: true,
  enableSubmitCase: true,
  enableCaseStatusView: true,
  enableTermQuoterQuickLink: false,
  enableGULQuoterQuickLink: false,
  enableSPWLQuoterQuickLink: false,
  enableIULEstimatorQuickLink: false,
  enableULEstimatorQuickLink: false,
  enableLTCEstimatorQuickLink: false,
  enableTermCard: false,
  enableSPLCard: false,
  enableAssetBasedLTCCard: false,
  enableTraditionalLTCCard: false,
  enableWholeLifeCard: false,
  enableDisabilityCard: false,
  enableIUL: false,
  enableGUL: false,
  enableUL: false,
  enableVUL: false,
  enableWLSimplifiedIssue: false,
  enableWLTraditional: false,
  enableSaveMyQuote: true,
  enableSaveMyCase: true,
  enablePermPurchasingStatistics: true,
  enablePermInsPlanningConcepts: true,
  enablePermProductSelector: true,
  enablePermPage: false,
};

export function useCheckPermission() {
  const { data } = useAccountSettings();
  return useCallback(
    (key: keyof AccountSettings): boolean => {
      const fromAccount = data?.[key];
      if (fromAccount != null) return fromAccount;
      return PermissionDefaults[key] ?? false;
    },
    [data],
  );
}
